import React, { useId } from 'react';
import { formatKronor } from './milestonePlan';
import { useTranslation } from 'react-i18next';
import { ContractDocumentPreview, type ContractDocumentPreviewProps } from './ContractDocumentPreview';
import { CUSTOMER_MESSAGE_MAX, type ReviewStepState } from './useReviewStep';
import { DraftMessageHistory, type DraftMessage } from './DraftMessageHistory';

export interface ReviewStepEdit {
  /** The contract was out for signing: this is a new version for both parties to sign. */
  resend: boolean;
  /** "Ändringar sedan version N", already translated. */
  changes: string[];
  /** The version that was sent. */
  version?: number;
}

interface ReviewStepProps {
  review: ReviewStepState;
  preview: ContractDocumentPreviewProps;
  stepCounter: string;
  providerName: string;
  /** Edit mode; omit when creating a contract. */
  edit?: ReviewStepEdit;
  variationDeltaOre?: number;
  refundOnSigning?: boolean;
  /** The draft conversation with the Beställare so far (returns, comments). */
  messages?: DraftMessage[];
  /**
   * "Lägg till beställare" while the project has none (ClickUp 869fn1nyr): at
   * the top of the right-hand panel, and above the contract on a phone so it
   * is not left below the whole preview.
   */
  customerPanel?: React.ReactNode;
}

/** Wizard step 3 — "Granska & signera": the contract as the customer sees it, plus the signing panel. */
export function ReviewStep({ review, preview, stepCounter, providerName, edit, variationDeltaOre, refundOnSigning, messages = [], customerPanel }: ReviewStepProps) {
  const { t } = useTranslation();
  const ids = useId();
  const missingReview = review.showErrors && !review.reviewed;
  const resend = !!edit?.resend;
  const showChanges = resend && (edit?.changes.length ?? 0) > 0;
  const initials = (preview.customerName ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('');

  return (
    <div className="contract-wizard-step">
      <header className="mb-4">
        <p className="contract-wizard-kicker mb-1">{stepCounter}</p>
        <h2 className="contract-wizard-step-title">{t('contracts.wizard.steps.review.title')}</h2>
        <p className="text-muted mb-0">{t(resend ? 'contracts.wizard.edit.review.intro' : 'contracts.wizard.review.intro')}</p>
      </header>

      {showChanges && edit && (
        <section className="contract-wizard-changes mb-4" aria-labelledby={`${ids}-changes`}>
          <h3 id={`${ids}-changes`} className="contract-wizard-changes-title">
            {edit.version
              ? t('contracts.wizard.edit.review.changesTitle', { version: edit.version })
              : t('contracts.wizard.edit.review.changesTitleNoVersion')}
          </h3>
          <ul className="contract-wizard-changes-list">
            {edit.changes.map((change, index) => <li key={index}>{change}</li>)}
          </ul>
        </section>
      )}

      <div className={`contract-wizard-review${customerPanel ? ' has-customer' : ''}`}>
        {customerPanel && (
          <div className="contract-wizard-review-customer" data-testid="contract-wizard-review-customer">{customerPanel}</div>
        )}
        <ContractDocumentPreview
          {...preview}
          chipLabel={showChanges ? t('contracts.wizard.edit.review.newVersionChip') : undefined}
        />

        <aside className="contract-wizard-sign-panel" aria-labelledby={`${ids}-sign`}>
          <p className="contract-wizard-kicker mb-1">{t('contracts.wizard.review.signKicker')}</p>
          <h3 id={`${ids}-sign`} className="contract-wizard-section-title">{t(resend ? 'contracts.wizard.edit.review.signTitle' : 'contracts.wizard.review.signTitle')}</h3>

          <div className="contract-wizard-recipient">
            <span className="contract-wizard-recipient-avatar" aria-hidden="true">{initials || '?'}</span>
            <span className="contract-wizard-file-name">
              <span className="d-block fw-semibold text-truncate">{preview.customerName || '—'}</span>
              <span className="d-block small text-muted text-truncate">{t('contracts.wizard.review.recipientRole')}</span>
            </span>
          </div>
          {preview.customerName && (
            // The Beställare is locked once sent (ClickUp 869fmxjgt, board 6).
            <p className="small text-muted mb-0" data-testid="contract-wizard-customer-lock">
              <i className="feather-lock me-2" aria-hidden="true"></i>
              {t('contracts.wizard.review.customerLock', { name: preview.customerName })}
            </p>
          )}

          <ol className="contract-wizard-sign-steps">
            <li>{t('contracts.wizard.review.stepYouSign', { provider: providerName })}</li>
            <li>{t('contracts.wizard.review.stepCustomer')}</li>
            {variationDeltaOre !== undefined ? <>
              <li>{t(variationDeltaOre > 0 ? 'variationWizard.customerPays' : variationDeltaOre < 0 ? 'variationWizard.customerRefund' : 'variationWizard.noPayment')}</li>
              <li>{t('variationWizard.applies')}</li>
            </> : <li>{t('contracts.wizard.review.stepStart')}</li>}
          </ol>

          {variationDeltaOre !== undefined && variationDeltaOre < 0 && (
            <div className="alert alert-info" data-testid="negative-variation-confirmation">
              <p>{t('projectView.ata.negativeConfirmIntro', { amount: formatKronor(Math.abs(variationDeltaOre) / 100) })}</p>
              <p>{t(refundOnSigning ? 'variationWizard.refundAfterSigning' : 'projectView.ata.negativeConfirmExact')}</p>
              <p className="mb-0">{t('projectView.ata.negativeConfirmNoFee')}</p>
            </div>
          )}

          <DraftMessageHistory messages={messages} />

          <div>
            <label htmlFor={`${ids}-msg`} className="form-label small fw-semibold">{t('contracts.customerComment.label')}</label>
            <textarea
              id={`${ids}-msg`}
              className="form-control"
              rows={3}
              value={review.message}
              maxLength={CUSTOMER_MESSAGE_MAX}
              placeholder={t('contracts.wizard.review.messagePlaceholder')}
              onChange={event => review.setMessage(event.target.value)}
            />
            <span className="form-text">{t('contracts.wizard.assignment.counter', { count: review.message.length, max: CUSTOMER_MESSAGE_MAX })}</span>
          </div>

          <div className={`contract-wizard-confirm${missingReview ? ' is-invalid' : ''}`}>
            <div className="form-check contract-wizard-check mb-0">
              <input
                id={`${ids}-reviewed`}
                type="checkbox"
                className="form-check-input"
                checked={review.reviewed}
                onChange={event => review.setReviewed(event.target.checked)}
                aria-invalid={missingReview || undefined}
                aria-describedby={missingReview ? `${ids}-reviewed-error` : undefined}
              />
              <label className="form-check-label" htmlFor={`${ids}-reviewed`}>
                {t('contracts.wizard.review.confirmLabel')}
              </label>
            </div>
            {missingReview && (
              <p id={`${ids}-reviewed-error`} className="text-danger small mb-0 mt-2" role="alert">
                {t('contracts.wizard.review.confirmRequired')}
              </p>
            )}
          </div>

          <p className="small text-muted mb-0">{t('contracts.actions.redirectNotice', { provider: providerName })}</p>

          {review.signError && (
            <div className="alert alert-danger mb-0" role="alert">{review.signError}</div>
          )}
        </aside>
      </div>
    </div>
  );
}

export default ReviewStep;
